(() => {
  'use strict';

  const SELECTOR = 'variant-selects';
  const INITIALIZED = 'nativeCheckoutCompatInitialized';

  function parseVariants(picker) {
    if (picker.__nativeCheckoutVariants) return picker.__nativeCheckoutVariants;
    const node = picker.querySelector('script[type="application/json"]');
    if (!node) return [];
    try {
      picker.__nativeCheckoutVariants = JSON.parse(node.textContent || '[]');
    } catch (error) {
      console.warn('[Native checkout] Could not parse product variants.', error);
      picker.__nativeCheckoutVariants = [];
    }
    return picker.__nativeCheckoutVariants;
  }

  function optionGroups(picker) {
    return Array.from(picker.querySelectorAll('.product-form__input'));
  }

  function selectedValue(group) {
    const select = group.querySelector('select');
    if (select) return select.value;
    const checked = group.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : null;
  }

  function selectedOptions(picker) {
    return optionGroups(picker).map(selectedValue);
  }

  function findVariant(picker) {
    const selected = selectedOptions(picker);
    if (selected.some(value => value == null)) return null;
    return parseVariants(picker).find(variant =>
      Array.isArray(variant.options) &&
      variant.options.length === selected.length &&
      variant.options.every((value, index) => value === selected[index])
    ) || null;
  }

  function sectionProductForms(sectionId) {
    const escaped = window.CSS && CSS.escape ? CSS.escape(sectionId) : sectionId;
    return Array.from(document.querySelectorAll(`product-form[data-section="${escaped}"]`));
  }

  function syncVariantId(sectionId, variant) {
    sectionProductForms(sectionId).forEach(productForm => {
      productForm.querySelectorAll('input.product-variant-id[name="id"]').forEach(input => {
        input.disabled = false;
        if (variant && String(input.value) !== String(variant.id)) {
          input.value = variant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  function setButtonState(sectionId, variant) {
    sectionProductForms(sectionId).forEach(productForm => {
      const button = productForm.querySelector('.product-form__submit');
      const label = button && button.querySelector('.main-atc__label');
      const price = button && button.querySelector('[id^="main-atc-price-"]');
      const text = button && button.querySelector('.main-atc__label__text');
      const checkout = productForm.querySelector('[data-native-dynamic-checkout]');
      const available = Boolean(variant && variant.available);

      if (checkout) checkout.hidden = !available;

      if (!button) return;
      button.toggleAttribute('disabled', !available);
      button.dataset.unavailable = available ? 'false' : 'true';

      if (available) {
        if (text) text.textContent = window.variantStrings?.addToCart || 'Add to cart';
        if (!text && label) label.textContent = window.variantStrings?.addToCart || 'Add to cart';
        if (price) price.hidden = false;
      } else {
        if (price) price.hidden = true;
        if (text) text.textContent = variant ? (window.variantStrings?.soldOut || 'Sold out') : (window.variantStrings?.unavailable || 'Unavailable');
        if (!text && label) label.textContent = variant ? (window.variantStrings?.soldOut || 'Sold out') : (window.variantStrings?.unavailable || 'Unavailable');
      }
    });
  }

  function refreshOptionAvailability(picker) {
    const variants = parseVariants(picker);
    const groups = optionGroups(picker);
    const selected = selectedOptions(picker);

    groups.forEach((group, optionIndex) => {
      const prefix = selected.slice(0, optionIndex);
      const select = group.querySelector('select');
      const controls = select ? Array.from(select.options) : Array.from(group.querySelectorAll('input[type="radio"]'));

      controls.forEach(control => {
        const value = control.value;
        const canBuy = variants.some(variant => {
          if (!variant.available || !Array.isArray(variant.options)) return false;
          if (variant.options[optionIndex] !== value) return false;
          return prefix.every((prefixValue, prefixIndex) => variant.options[prefixIndex] === prefixValue);
        });

        // Keep the currently-selected value selectable long enough for Shopify's own
        // variant handler to finish, but prevent customers choosing known dead ends.
        const isSelected = select ? select.value === value : control.checked;
        control.disabled = !canBuy && !isSelected;
        if (!select) control.classList.toggle('disabled', !canBuy);
      });
    });
  }

  function chooseVariantControls(picker, variant) {
    if (!variant || !Array.isArray(variant.options)) return false;
    const groups = optionGroups(picker);
    let lastControl = null;

    groups.forEach((group, index) => {
      const value = variant.options[index];
      const select = group.querySelector('select');
      if (select) {
        const option = Array.from(select.options).find(item => item.value === value);
        if (option) {
          option.disabled = false;
          select.value = value;
          lastControl = select;
        }
        return;
      }

      const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(item => item.value === value);
      if (radio) {
        radio.disabled = false;
        radio.classList.remove('disabled');
        radio.checked = true;
        lastControl = radio;
      }
    });

    if (lastControl) {
      lastControl.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  }

  function updateUrl(picker, variant) {
    if (!variant || picker.dataset.updateUrl === 'false' || !window.history?.replaceState) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch (_) {}
  }

  function syncPicker(picker) {
    const variant = findVariant(picker);
    const sectionId = picker.dataset.section;
    if (!sectionId) return;
    syncVariantId(sectionId, variant);
    setButtonState(sectionId, variant);
    refreshOptionAvailability(picker);
  }

  function normalizeAfterChange(picker, changedControl) {
    if (picker.__nativeCheckoutAdjusting) {
      syncPicker(picker);
      return;
    }

    const current = findVariant(picker);
    if (current && current.available) {
      syncPicker(picker);
      return;
    }

    const groups = optionGroups(picker);
    const changedGroup = changedControl?.closest?.('.product-form__input');
    const changedIndex = changedGroup ? groups.indexOf(changedGroup) : -1;
    const selected = selectedOptions(picker);
    const variants = parseVariants(picker);

    // Preserve the option the customer just changed, then move only later options
    // to the first Shopify-sellable combination. This avoids a Type change leaving
    // an old Size selected that makes the page look SOLD OUT.
    const prefixLength = changedIndex >= 0 ? changedIndex + 1 : 0;
    let fallback = null;
    if (prefixLength > 0) {
      fallback = variants.find(variant =>
        variant.available &&
        Array.isArray(variant.options) &&
        selected.slice(0, prefixLength).every((value, index) => variant.options[index] === value)
      );
    }

    if (!fallback && selected[0] != null) {
      fallback = variants.find(variant =>
        variant.available && Array.isArray(variant.options) && variant.options[0] === selected[0]
      );
    }

    if (!fallback) {
      syncPicker(picker);
      return;
    }

    picker.__nativeCheckoutAdjusting = true;
    syncVariantId(picker.dataset.section, fallback);
    setButtonState(picker.dataset.section, fallback);
    updateUrl(picker, fallback);
    chooseVariantControls(picker, fallback);
    window.setTimeout(() => {
      picker.__nativeCheckoutAdjusting = false;
      syncPicker(picker);
    }, 220);
  }

  function initialFallback(picker) {
    const variants = parseVariants(picker);
    if (!variants.length) return;

    const sectionId = picker.dataset.section;
    const productForm = sectionProductForms(sectionId)[0];
    const idInput = productForm?.querySelector('input.product-variant-id[name="id"]');
    const currentId = idInput?.value || new URLSearchParams(window.location.search).get('variant');
    const current = variants.find(variant => String(variant.id) === String(currentId)) || findVariant(picker);

    if (current && current.available) {
      syncPicker(picker);
      return;
    }

    const firstAvailable = variants.find(variant => variant.available);
    if (!firstAvailable) {
      syncPicker(picker);
      return;
    }

    // If an ad/product URL points at an unavailable SKU, don't strand the customer
    // on a false-looking SOLD OUT state when another Shopify-sellable variant exists.
    syncVariantId(sectionId, firstAvailable);
    setButtonState(sectionId, firstAvailable);
    updateUrl(picker, firstAvailable);
    chooseVariantControls(picker, firstAvailable);

    // Let the theme's native VariantSelects handler finish its own render first.
    window.setTimeout(() => syncPicker(picker), 120);
    window.setTimeout(() => syncPicker(picker), 500);
  }

  function initPicker(picker) {
    if (!picker || picker.dataset[INITIALIZED] === 'true') return;
    picker.dataset[INITIALIZED] = 'true';

    picker.addEventListener('change', event => {
      // The theme's existing handler is obfuscated and runs on the same event. Queue
      // our form/payment synchronization after it instead of replacing it.
      const changedControl = event.target;
      window.setTimeout(() => normalizeAfterChange(picker, changedControl), 0);
      window.setTimeout(() => syncPicker(picker), 180);
    });

    initialFallback(picker);
  }

  function init(root = document) {
    root.querySelectorAll?.(SELECTOR).forEach(initPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', event => init(event.target));
})();
